import { bindActionCreators } from 'redux';
import { FormikErrors } from 'formik';
import { FormValues } from '@ahmdigital/form-validation/lib/validators/types';
import { pick } from 'lodash/fp';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { useState } from 'react';

import { EMBEDDED_QUOTE_LEADS_CAPTURE_FEATURE } from '../../../../ahm-constants/ui';
import { FIND_COVER_BUTTON_TEXT, MODAL_FIELDS, WIDGET_TITLES } from '../../data/constants';
import { getHealthWidgetData } from '../../data';
import { HealthFormValues } from '../../types';
import { useFeatureIsOn } from '../../../../../../utils/growth-book/hooks';
import { WIDGET_IDS } from '../../../../components/embedded-widget/constants';
import constants from '../../../../ahm-constants';
import customerActions from '../../../../actions/customer';
import device from '../../../../utils/device';
import EmbeddedWidget from '../../../../components/embedded-widget';
import generateSchema from './schema';
import getDestinationUrl from '../../../refine-price/utils/get-destination-url';
import selectCustomer from '../../../../selectors/select-customer';
import selectCustomerFormInitialValues, {
  getNormalizedProductType,
} from '../../../../components/pricing-drawer/selectors/select-customer-form-initial-values';
import submitEmbeddedQuoteAnalytics from './utils/submit-embedded-quote-analytics';
import submitRefinePrice from '../../../refine-price/utils/submit-refine-price';
import submitRefinePriceLead from '../../../refine-price/utils/submit-refine-price-lead';
import useQueryProductType from '../../../acquisition-landing/hooks/use-query-product-type';
import useViewportValue from '../../../../utils/use-viewport-value';

type HealthWidgetProps = {
  variant?: 'hero' | 'inline';
};

const HEALTH_WIDGET_VARIANT_CONFIG = {
  hero: {
    widgetId: WIDGET_IDS.PHI_HOME,
  },
  inline: {
    widgetId: WIDGET_IDS.PHI_HEADER,
  },
} as const;

const HealthWidget = ({ variant = 'hero' }: HealthWidgetProps) => {
  const customer = useSelector(selectCustomer);
  const customerFormInitialValues = useSelector(selectCustomerFormInitialValues) as HealthFormValues;
  const queryProductType = useQueryProductType();
  const leadCaptureDisabled = useFeatureIsOn(EMBEDDED_QUOTE_LEADS_CAPTURE_FEATURE);

  const router = useRouter();

  const [isHealthWidgetActive, setIsHealthWidgetActive] = useState(false);

  const dispatch = useDispatch();

  const breakpoint = useViewportValue(device.getBreakpoint);
  const { widgetId } = HEALTH_WIDGET_VARIANT_CONFIG[variant];

  const handleSubmit = (values: FormValues) => {
    const url = getDestinationUrl((values as HealthFormValues).productType);
    const WIDGET_FIELDS = [
      'age',
      'partnerAge',
      'productType',
      'scale',
      'state',
      'incomeTier',
      'incomeType',
      'rebate',
      'email',
      'name',
      'phone',
    ];
    const formValues = pick(WIDGET_FIELDS, values) as HealthFormValues;
    const initialValues = pick(WIDGET_FIELDS, customerFormInitialValues) as HealthFormValues;
    if (!leadCaptureDisabled) {
      submitEmbeddedQuoteAnalytics(formValues);
      submitRefinePriceLead({ formValues, leadSourceType: constants.LEAD_SOURCE_TYPES.EMBEDDED_QUOTE });
    }
    submitRefinePrice({
      actions: { customer: bindActionCreators(customerActions, dispatch) },
      customer,
      formValues,
      initialValues,
    });

    router.push(url);
  };

  return (
    <EmbeddedWidget
      buttonSpacing={{ lg: 2.5, md: 1.8 }}
      fluidWidth
      title={WIDGET_TITLES}
      getCustomParamValues={() => {
        if (!queryProductType) {
          return {};
        }

        return {
          productType: getNormalizedProductType(queryProductType),
        };
      }}
      getData={(widgetValues) => getHealthWidgetData(widgetValues, customerFormInitialValues, breakpoint)}
      getSchema={(widgetErrors: FormikErrors<HealthFormValues>) =>
        generateSchema({
          age: customerFormInitialValues.age,
          email: '',
          incomeTier: customerFormInitialValues.incomeTier,
          incomeType: customerFormInitialValues.incomeType,
          name: '',
          partnerAge: customerFormInitialValues.partnerAge,
          phone: '',
          productType: customerFormInitialValues.productType as HealthFormValues['productType'],
          rebate: customerFormInitialValues.rebate,
          scale: customerFormInitialValues.scale,
          state: customerFormInitialValues.state,
          widgetErrors,
        })
      }
      handleSubmit={handleSubmit}
      isWidgetActive={isHealthWidgetActive}
      modalFields={MODAL_FIELDS}
      setIsWidgetActive={setIsHealthWidgetActive}
      stepInputAppearance={{ mdNonLastPaddingXRem: 0.8 }}
      submitButton={{
        analyticsLabel: `${FIND_COVER_BUTTON_TEXT} - Embedded quote`,
        text: FIND_COVER_BUTTON_TEXT,
      }}
      wrapperPadding="0.8rem"
      widgetId={widgetId}
      showFieldsRequiredLabel={false}
    />
  );
};

export default HealthWidget;
